import { Link } from "react-router-dom";
import { MdCallEnd } from "react-icons/md";
import { MdDragIndicator } from "react-icons/md";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  BsFillCameraVideoFill,
  BsFillCameraVideoOffFill,
  BsMicFill,
  BsMicMuteFill,
} from "react-icons/bs";

import type { CallDto } from "~/data/call/call.dto";
import type { UserTestDto } from "~/data/user-test/user-test.dto";

import { socket } from "~/data/core/socket";

import { cn } from "../shared/utils/cn";
import { Button } from "../shared/button";
import { UserAvatar } from "../shared/user-avatar";
import { IconButton } from "../shared/icon-button";
import { useUser } from "../shared/queries/user-query";
import { MinimizeIcon } from "../shared/icons/minimize";
import { MaximizeIcon } from "../shared/icons/maximize";
import { useMediaStream } from "../shared/utils/use-media-stream";

interface Props {
  call: CallDto;
  onCallFinish(): void;
  onTestStart(data: UserTestDto): void;
}

export function VideoCall({ call, onCallFinish, onTestStart }: Props) {
  const user = useUser();
  useEffect(() => socket.on("finish_video_chat", onCallFinish), [onCallFinish]);

  const [hasActiveTest, setHasActiveTest] = useState(false);
  const [selectingTest, setSelectingTest] = useState(false);
  const [waitingSelectTest, setWaitingSelectTest] = useState(false);
  const [minimized, toggleMinimized] = useReducer((state) => !state, false);
  useEffect(
    () =>
      socket.on("new_user_test", (data) => {
        setHasActiveTest(true);
        setSelectingTest(false);
        onTestStart(data);
        socket.send({
          command: "message",
          identifier: socket.channels.video(call.id),
          data: { action: "signal", selecting_next: false },
        });
      }),
    [call.id, onTestStart]
  );
  useEffect(() => socket.on("finish_test", () => setHasActiveTest(false)), []);
  useEffect(
    () =>
      socket.on("signal", (data) => {
        if (data.from && data.from === user.id) return; // ignore event sent by myself
        setWaitingSelectTest((curr) => data.selecting_next ?? curr);
      }),
    [user.id]
  );

  const [mateUnloaded, setMateUnloaded] = useState(false);
  useEffect(
    () =>
      socket.on("signal", (data) => {
        if (data.unload) setMateUnloaded(true);
      }),
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({
    bottom: 24,
    right: 24,
  });

  const handleStart = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return;
    startRef.current.x = container.offsetLeft - clientX;
    startRef.current.y = container.offsetTop - clientY;
    setDragging(true);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    handleStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    }
  };

  useEffect(() => {
    if (!dragging) return;

    const controller = new AbortController();
    const { signal } = controller;

    window.addEventListener(
      "mousemove",
      (e) => {
        const container = containerRef.current;
        if (!container) return;
        const offset = startRef.current;
        const root = document.documentElement;
        const left = Math.min(
          Math.max(e.clientX + offset.x, 0),
          root.clientWidth - container.offsetWidth
        );
        const top = Math.min(
          Math.max(e.clientY + offset.y, 0),
          root.clientHeight - container.offsetHeight
        );
        setPosition({ left, top });
      },
      { signal }
    );

    window.addEventListener(
      "touchmove",
      (e) => {
        if (e.touches.length > 0) {
          e.preventDefault();
          const touch = e.touches[0];
          const container = containerRef.current;
          if (!container) return;
          const offset = startRef.current;
          const root = document.documentElement;
          const left = Math.min(
            Math.max(touch.clientX + offset.x, 0),
            root.clientWidth - container.offsetWidth
          );
          const top = Math.min(
            Math.max(touch.clientY + offset.y, 0),
            root.clientHeight - container.offsetHeight
          );
          setPosition({ left, top });
        }
      },
      { signal, passive: false }
    );

    window.addEventListener("mouseup", () => setDragging(false), { signal });

    window.addEventListener("touchend", () => setDragging(false), { signal });

    return () => controller.abort();
  }, [dragging]);

  // Normalizes position automatically when maximizing
  useEffect(() => {
    const root = document.documentElement;
    const container = containerRef.current;
    if (!container) return;

    setPosition((position) => {
      if (position.left === undefined || position.top === undefined)
        return position;
      const originalLeft = +position.left;
      const originalTop = +position.top;
      const left = Math.min(
        Math.max(originalLeft, 0),
        root.clientWidth - container.offsetWidth
      );
      const top = Math.min(
        Math.max(originalTop, 0),
        root.clientHeight - container.offsetHeight
      );
      return { left, top };
    });
  }, [minimized]);

  const mate = call.user1.id === user.id ? call.user2! : call.user1;
  const isMateActor = mate?.actor; // checks if the call mate is always an actor
  const isCaller = user.id === call.user1.id;

  return (
    <div
      ref={containerRef}
      className="fixed z-40 p-5 rounded-[20px] bg-white/70 backdrop-blur grid gap-5"
      style={{
        gridTemplateRows: minimized
          ? "1fr"
          : hasActiveTest
          ? "auto 1fr"
          : "auto 1fr auto",
        padding: minimized ? 0 : 20,
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        width: `min(calc(100% - 48px), ${minimized ? "172px" : "345px"})`,
        height: `min(calc(100% - 48px), ${minimized ? "48px" : "634px"})`,
        ...position,
      }}
    >
      <button
        type="button"
        className={cn(
          "absolute rotate-90 z-10",
          minimized
            ? "text-white left-3 top-1/2 -translate-y-1/2"
            : "left-4 top-4"
        )}
        title={minimized ? "Maximizar" : "Minimizar"}
        onClick={toggleMinimized}
      >
        {minimized ? <MaximizeIcon /> : <MinimizeIcon />}
      </button>
      {minimized && (
        <div
          data-dragging={dragging}
          className="absolute z-10 top-1/2 -translate-y-1/2 right-3 text-white cursor-grab data-[dragging=true]:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <MdDragIndicator size={16} />
        </div>
      )}

      {!minimized && (
        <div>
          <div className="flex justify-between gap-7 py-[10px] px-4">
            <UserAvatar url={mate.avatar_url} name={mate.name} size={44} />
            <div className="flex-1 grid">
              <p className="truncate">{mate.name}</p>
            </div>
            <div
              data-dragging={dragging}
              className="cursor-grab data-[dragging=true]:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <MdDragIndicator size={24} />
            </div>
          </div>

          {mateUnloaded && (
            <p className="text-center text-xs font-semibold text-red-700 absolute left-1/2 -translate-x-1/2">
              Seu colega desconectou.
            </p>
          )}
        </div>
      )}

      <VideoCallConnection
        room={call.id}
        autoConnect={!isCaller}
        onClose={onCallFinish}
        minimized={minimized}
      />

      {!minimized &&
        !waitingSelectTest &&
        !hasActiveTest &&
        (isMateActor ? (
          <p className="text-center text-sm px-2">
            Você não pode iniciar, seu colega é ator
          </p>
        ) : selectingTest ? (
          <Button
            disabled
            variant="default"
            className="h-10 w-full data-[variant=default]:text-[#979797]"
          >
            Favor Selecionar o Checklist
          </Button>
        ) : (
          <Button
            asChild
            variant="primary-2"
            className="h-10 w-full"
            onClick={() => {
              setSelectingTest(true);
              socket.send({
                command: "message",
                identifier: socket.channels.video(call.id),
                data: { action: "signal", selecting_next: true, from: user.id },
              });
            }}
          >
            <Link to="/cenarios">Iniciar novo exercício</Link>
          </Button>
        ))}
    </div>
  );
}

export function VideoCallConnection({
  room,
  autoConnect,
  onClose,
  minimized,
}: {
  room: string;
  autoConnect?: boolean;
  onClose?: () => void;
  minimized?: boolean;
}) {
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection>(null);
  const candidatesRef = useRef<RTCIceCandidate[]>([]);
  const [connectionState, setConnectionState] =
    useState<RTCPeerConnectionState>();
  const { id: userId } = useUser();

  const [localConnectionScore, setLocalConnectionScore] = useState(0);
  const [remoteConnectionScore, setRemoteConnectionScore] = useState(0);
  useEffect(() => {
    const interval = setInterval(async function monitorConnection() {
      const peerConnection = peerConnectionRef.current;
      if (!peerConnection) return;

      if (["disconnected", "failed"].includes(peerConnection.connectionState)) {
        setLocalConnectionScore(0);
      }

      const stats = await peerConnection.getStats(null);
      stats.forEach((report) => {
        if (report.type === "candidate-pair" && report.state === "succeeded") {
          const rtt = report.currentRoundTripTime
            ? report.currentRoundTripTime * 1000
            : undefined;
          const bitrate = report.availableOutgoingBitrate
            ? report.availableOutgoingBitrate / 1000
            : undefined;

          stats.forEach((subReport) => {
            if (
              subReport.type === "inbound-rtp" &&
              subReport.kind === "video"
            ) {
              const score = calculateConnectionScore({
                rtt,
                bitrate,
                packetsLost: subReport.packetsLost,
                packetsReceived: subReport.packetsReceived,
              });
              setLocalConnectionScore(score);
              sendScore(score);
            }
          });
        }
      });
      function calculateConnectionScore({
        rtt,
        packetsLost,
        packetsReceived,
        bitrate,
      }: any) {
        // Fallbacks para evitar NaN
        rtt = typeof rtt === "number" ? rtt : 300;
        packetsLost = typeof packetsLost === "number" ? packetsLost : 0;
        packetsReceived =
          typeof packetsReceived === "number" ? packetsReceived : 1;
        bitrate = typeof bitrate === "number" ? bitrate : 100;

        const totalPackets = packetsLost + packetsReceived;
        const packetLoss = totalPackets > 0 ? packetsLost / totalPackets : 0;

        const latencyScore = Math.max(0, 1 - rtt / 500); // ideal < 200ms
        const lossScore = Math.max(0, 1 - packetLoss * 10); // ideal < 10%
        const bitrateScore = Math.min(1, bitrate / 300); // ideal > 300 kbps

        const score = (latencyScore + lossScore + bitrateScore) / 3;

        return Number(score.toFixed(2)); // garantir número
      }
      function sendScore(score: number) {
        socket.send({
          command: "message",
          identifier: socket.channels.video(room),
          data: {
            action: "signal",
            from: userId,
            score,
          },
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [room, userId]);
  useEffect(() => {
    let timeout = 0;
    const off = socket.on("signal", (data) => {
      if (data.score === undefined || data.from === userId) return;
      clearTimeout(timeout);
      setRemoteConnectionScore(data.score);
      timeout = setTimeout(() => setRemoteConnectionScore(0), 1.5 * 1000); // reset score if stop receiving score events
    });
    return () => {
      off();
      clearTimeout(timeout);
    };
  }, [userId]);

  useEffect(
    () =>
      socket.on("finish_video_chat", () => {
        peerConnectionRef.current?.close();
        peerConnectionRef.current = null;
      }),
    []
  );

  const localMediaStream = useMediaStream();
  useEffect(() => {
    localMediaStream.play();
    return () => localMediaStream.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMediaStream.play, localMediaStream.stop]);

  // Setup peer connection and signal events handling
  const createConnection = useCallback(
    (stream?: MediaStream | null) => {
      const connection = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      connection.ontrack = (event) => {
        const video = remoteVideoRef.current;
        if (!video) return;
        video.srcObject = event.streams[0];
        video.play();
      };
      connection.onicecandidate = (event) => {
        if (event.candidate) {
          socket.send({
            command: "message",
            identifier: socket.channels.video(room),
            data: {
              action: "signal",
              candidate: event.candidate,
              from: userId,
            },
          });
        }
      };
      console.log("Peer: CONNECTION STATE", connection.connectionState);
      console.log("Peer: SIGNALING STATE", connection.signalingState);
      connection.onconnectionstatechange = () => {
        console.log("Peer: CONNECTION STATE", connection.connectionState);
        setConnectionState(connection.connectionState);

        if (connection.connectionState === "failed") {
          // Might consider using .createOffer({ iceRestart: true }) for restablishing the connection
          // For now, just closing the current connection and creating a new one will be simpler
          peerConnectionRef.current?.close();
          peerConnectionRef.current = null;
        }
      };
      connection.onsignalingstatechange = () => {
        console.log("Peer: SIGNALING STATE", connection.signalingState);
      };
      setConnectionState(connection.connectionState);
      stream
        ?.getTracks()
        .forEach((track) => connection.addTrack(track, stream));
      return connection;
    },
    [room, userId]
  );

  useEffect(() => {
    // if (finished || !started) return;

    // Handle ws signal events
    return socket.on("signal", async (data) => {
      if (data.from === userId) return;
      // const type = data.offer
      //   ? "offer"
      //   : data.answer
      //   ? "answer"
      //   : data.candidate
      //   ? "candidate"
      //   : "unknown";
      // console.log("SIGNAL EVENT RECEIVED", type);

      if (data.offer) {
        console.log("on receive remote offer...");

        peerConnectionRef.current?.close();
        peerConnectionRef.current = null;

        const stream = await localMediaStream.play();
        peerConnectionRef.current = createConnection(stream);
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription(data.offer)
        );
        const answer = await peerConnectionRef.current!.createAnswer();
        await peerConnectionRef.current!.setLocalDescription(answer);

        socket.send({
          command: "message",
          identifier: socket.channels.video(room),
          data: { action: "signal", answer, from: userId },
        });
      }

      if (data.answer) {
        console.log("on receive remote answer...");
        await peerConnectionRef.current!.setRemoteDescription(
          new RTCSessionDescription(data.answer)
        );
        candidatesRef.current.forEach((candidate) =>
          peerConnectionRef.current!.addIceCandidate(
            new RTCIceCandidate(candidate)
          )
        );
      }

      if (data.candidate) {
        const remoteDescription = peerConnectionRef.current?.remoteDescription;
        if (remoteDescription && remoteDescription.type) {
          await peerConnectionRef.current!.addIceCandidate(
            new RTCIceCandidate(data.candidate)
          );
        } else {
          candidatesRef.current.push(data.candidate);
        }
      }
    });
  }, [userId, room, createConnection]);

  const ref = useRef(0);
  useEffect(() => {
    console.log("running effect...", { autoConnect, room, userId });
    if (!autoConnect) return;

    ref.current++;
    const effectId = ref.current;

    const startCall = async () => {
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;

      const stream = await localMediaStream.play();
      peerConnectionRef.current = createConnection(stream);
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);

      socket.send({
        command: "message",
        identifier: socket.channels.video(room),
        data: { action: "signal", offer, from: userId },
      });
      socket.send({
        command: "message",
        identifier: socket.channels.video(room),
        data: { action: "start_video_chat" },
      });
      console.log("video start requested....", effectId);
    };

    startCall();
  }, [autoConnect, createConnection, room, userId]);

  const finishCall = () => {
    peerConnectionRef.current?.close();
    setConnectionState(peerConnectionRef.current?.connectionState);
    socket.send({
      command: "message",
      identifier: socket.channels.video(room),
      data: { action: "finish_video_chat" },
    });
    onClose?.();
  };

  const [isCameraEnabled, setCameraEnabled] = useState(true);
  const [isMicrophoneEnabled, setMicrophoneEnabled] = useState(true);
  const toggleCamera = () => {
    const videoTrack = localMediaStream.stream.current
      ?.getVideoTracks()
      .find((track) => track.kind === "video");
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setCameraEnabled(videoTrack.enabled);
    }
  };
  const toggleMicrophone = () => {
    const audioTrack = localMediaStream.stream.current
      ?.getAudioTracks()
      .find((track) => track.kind === "audio");
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setMicrophoneEnabled(audioTrack.enabled);
    }
  };

  return (
    <div className="rounded-[10px] p-3 bg-black flex flex-col gap-3">
      <div
        data-hidden={minimized}
        className="relative flex-1 data-[hidden=true]:hidden"
      >
        <video
          ref={localMediaStream.ref}
          playsInline
          muted
          className="object-contain bg-gray-700 rounded-xl absolute top-0 left-0 w-full h-full"
        />
        <SignalBars score={localConnectionScore} />
      </div>
      <div
        data-hidden={minimized}
        className="relative flex-1 data-[hidden=true]:hidden"
      >
        <video
          ref={remoteVideoRef}
          playsInline
          className="object-contain bg-gray-700 rounded-xl absolute top-0 left-0 w-full h-full"
        />
        <SignalBars score={remoteConnectionScore} />
      </div>
      <div className="flex items-center justify-center gap-3">
        <IconButton
          aria-label="Toggle Mic"
          type="button"
          onClick={toggleMicrophone}
          icon={isMicrophoneEnabled ? <BsMicFill /> : <BsMicMuteFill />}
        />
        <IconButton
          aria-label="Toggle Cam"
          type="button"
          onClick={toggleCamera}
          icon={
            isCameraEnabled ? (
              <BsFillCameraVideoFill />
            ) : (
              <BsFillCameraVideoOffFill />
            )
          }
        />

        <IconButton
          aria-label={connectionState}
          type="button"
          onClick={finishCall}
          icon={<MdCallEnd />}
          style={{ color: "white", background: "#FA4F3B" }}
        />
      </div>
      {/* <div className="text-white text-center text-sm">{connectionState}</div> */}
    </div>
  );
}

function SignalBars({ score }: { score: number | null }) {
  const getColor = (level: number) => {
    if (score === null || score <= 0) return "bg-gray-400";
    if (score <= 0.25) return level === 1 ? "bg-red-500" : "bg-gray-300";
    if (score <= 0.5) return level <= 2 ? "bg-orange-400" : "bg-gray-300";
    if (score <= 0.75) return level <= 3 ? "bg-yellow-400" : "bg-gray-300";
    return "bg-green-500";
  };

  return (
    <div className="flex flex-col-reverse items-end gap-px absolute top-1 right-2 rotate-90">
      {[1, 2, 3, 4].map((level) => (
        <div
          key={level}
          className={`h-1 w-4 rounded transition-all ${getColor(level)}`}
          style={{ width: level * 3 }}
        />
      ))}
    </div>
  );
}
